import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { triggerFirstLoginEmail } from './lib/emailEvents';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AlbumPage from './pages/AlbumPage';
import LandingPage from './pages/LandingPage';
import InvitePage, { PENDING_INVITE_KEY } from './pages/InvitePage';
import MatchPage, { PENDING_MATCH_KEY } from './pages/MatchPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import ParceirosPage from './pages/ParceirosPage';
import TradePage from './pages/TradePage';
import { AchievementToast } from './components/AchievementToast';
import { UsernameGate } from './components/social/UsernameGate';
import { TutorialProvider } from './components/tutorial/TutorialProvider';

const INVITE_TOKEN_RE = /^[A-Za-z0-9_-]{8,128}$/;
const MATCH_DATA_RE = /^[A-Za-z0-9_-]{20,4096}$/;

function readSafe(key: string, validator: RegExp): string | null {
  const v = localStorage.getItem(key);
  return v && validator.test(v) ? v : null;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasUsername, setHasUsername] = useState<boolean | null>(null);
  const [pendingInviteToken, setPendingInviteToken] = useState<string | null>(
    () => readSafe(PENDING_INVITE_KEY, INVITE_TOKEN_RE)
  );
  const [pendingMatchData, setPendingMatchData] = useState<string | null>(
    () => readSafe(PENDING_MATCH_KEY, MATCH_DATA_RE)
  );

  useEffect(() => {
    const hasAuthTokenInUrl = window.location.hash.includes('access_token');

    if (!hasAuthTokenInUrl) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        setUser(session?.user ?? null);
        setLoading(false);
      });
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (event === 'SIGNED_IN') {
        const token = readSafe(PENDING_INVITE_KEY, INVITE_TOKEN_RE);
        if (token) setPendingInviteToken(token);
        const match = readSafe(PENDING_MATCH_KEY, MATCH_DATA_RE);
        if (match) setPendingMatchData(match);
        if (session?.user) {
          void triggerFirstLoginEmail(session.user);
        }
      }
      if (hasAuthTokenInUrl) {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setHasUsername(null);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        // TODO: revisar se schema usa profiles.user_id ou profiles.id
        setHasUsername(false);
        return;
      }
      setHasUsername(!!data?.username);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-700">
        <div className="text-white text-center">
          <Loader2 size={32} className="animate-spin mx-auto mb-3" strokeWidth={2.25} />
          <p className="font-body text-small text-white/80">Carregando...</p>
        </div>
      </div>
    );
  }

  const homeRedirect = user
    ? pendingMatchData
      ? <Navigate to={`/m/${pendingMatchData}`} replace />
      : pendingInviteToken
        ? <Navigate to={`/invite/${pendingInviteToken}`} replace />
        : <Navigate to="/album" replace />
    : <LandingPage />;

  return (
    <BrowserRouter>
      <TutorialProvider>
        <Routes>
          <Route path="/" element={homeRedirect} />
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <LoginPage />}
          />
          <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
          <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
          <Route
            path="/album"
            element={user ? <AlbumPage user={user} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/configuracoes"
            element={user ? <SettingsPage user={user} /> : <Navigate to="/login" replace />}
          />
          <Route path="/invite/:token" element={<InvitePage user={user} />} />
          <Route path="/convite/:slug/:token" element={<InvitePage user={user} />} />
          <Route path="/m/:data" element={<MatchPage user={user} />} />
          <Route
            path="/profile"
            element={user ? <ProfilePage user={user} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/profile/:userId"
            element={user ? <ProfilePage user={user} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/perfil/:slug"
            element={user ? <ProfilePage user={user} /> : <Navigate to="/login" replace />}
          />
          <Route path="/parceiros" element={<ParceirosPage publicMode={!user} user={user} />} />
          <Route
            path="/troca/:tradeId"
            element={user ? <TradePage user={user} /> : <Navigate to="/login" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {user && <AchievementToast user={user} />}
        {user && hasUsername === false && (
          <UsernameGate user={user} onComplete={() => setHasUsername(true)} />
        )}
      </TutorialProvider>
    </BrowserRouter>
  );
}

export default App;
